import { useEffect, useRef, useState } from "react";
import type { LineageRow } from "../api/types";
import { type Basis, computeBasis, fitnessColorRgb, projectRow } from "./pca";

const NS = "http://www.w3.org/2000/svg";
const R_MAX = 90;
const PHYLO_WINDOW = 100;
const WARMUP = 5;
const PHYLO_ITERS = 32;

interface Stats {
  gen: number; best: number; genBest: number; genMean: number; sigma: number;
  div: string; drift: string; pc1: string; pc2: string; dim: number; k: number;
  frozen: boolean;
}

function el(tag: string, attrs: Record<string, string>): SVGElement {
  const e = document.createElementNS(NS, tag);
  for (const k in attrs) e.setAttribute(k, attrs[k]);
  return e;
}

function leavesOf(r: LineageRow): number[][] {
  return r.top_k && r.top_k.length > 0 ? r.top_k.map((t) => t.genome) : [r.best];
}
function fitsOf(r: LineageRow): number[] {
  return r.top_k && r.top_k.length > 0 ? r.top_k.map((t) => t.fitness) : [r.fitness_gen_best];
}
function stdEuclid(basis: Basis, a: number[], b: number[]): number {
  let s = 0;
  for (let j = 0; j < a.length; j++) {
    const d = (a[j] - b[j]) / basis.std[j];
    s += d * d;
  }
  return Math.sqrt(s);
}

export function PhyloSvg({ lineage }: { lineage: LineageRow[] }) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const basisRef = useRef<Basis | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || lineage.length === 0) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const lin = lineage.length > PHYLO_WINDOW ? lineage.slice(-PHYLO_WINDOW) : lineage;
    const N = lin.length;
    const meanRows = lin.map((r) => r.mean);
    const leafRowsByGen = lin.map(leavesOf);

    // Freeze the PCA basis after WARMUP gens (matches the desktop console). Also recompute if the
    // frozen basis dimension no longer matches the genome length — otherwise a basis frozen on an
    // older run (e.g. 224p) projects the current run's longer vectors (254p) past its array end →
    // NaN → a blank plot. (Defensive; the key={name} remount normally resets this on run switch.)
    if (basisRef.current === null || basisRef.current.basisSize < WARMUP
        || basisRef.current.mean.length !== meanRows[0].length) {
      const warmup: number[][] = [];
      for (const m of meanRows) warmup.push(m);
      for (const leaves of leafRowsByGen) for (const g of leaves) warmup.push(g);
      const basis = computeBasis(warmup, PHYLO_ITERS);
      basisRef.current = lineage.length >= WARMUP ? basis : { ...basis, basisSize: lineage.length };
    }
    const basis = basisRef.current;

    const meanProj = meanRows.map((m) => projectRow(basis, m));
    const leafProjByGen = leafRowsByGen.map((leaves) => leaves.map((g) => projectRow(basis, g)));

    let fMin = Infinity, fMax = -Infinity, maxGen = 1, minGen = Infinity;
    for (let i = 0; i < N; i++) {
      const r = lin[i];
      if (r.gen > maxGen) maxGen = r.gen;
      if (r.gen < minGen) minGen = r.gen;
      for (const f of fitsOf(r)) { if (f < fMin) fMin = f; if (f > fMax) fMax = f; }
    }
    if (!isFinite(minGen)) minGen = 0;
    if (!isFinite(fMin)) { fMin = 0; fMax = 1; }
    const fRange = fMax - fMin || 1;

    for (let i = 1; i <= 4; i++) {
      const r = (R_MAX * i) / 4;
      svg.appendChild(el("circle", { cx: "0", cy: "0", r: `${r}`, fill: "none", stroke: "#1e1e1e", "stroke-width": "0.3" }));
      const lbl = el("text", { x: "0", y: `${-r - 1.2}`, fill: "#666", "font-family": "monospace", "font-size": "3.2", "text-anchor": "middle" });
      lbl.textContent = `gen ${Math.round(minGen + (maxGen - minGen) * i / 4)}`;
      svg.appendChild(lbl);
    }

    const LEAF_OFF = 6;
    const radiusMean = (g: number) => Math.max(2, ((g - minGen) / ((maxGen - minGen) || 1)) * R_MAX);
    const radiusLeaf = (g: number) => radiusMean(g) + LEAF_OFF;
    const angleOf = (a: number, b: number) => Math.atan2(b, a);
    const polar = (a: number, b: number, r: number) => { const th = angleOf(a, b); return [r * Math.cos(th), r * Math.sin(th)]; };
    const meanPts = meanProj.map((p, i) => polar(p[0], p[1], radiusMean(lin[i].gen)));

    const trunk = el("polyline", {
      points: meanPts.map((p) => `${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(" "),
      fill: "none", stroke: "#4a9eff", "stroke-width": "1.0", "stroke-opacity": "0.5", "stroke-linejoin": "round",
    });
    svg.appendChild(trunk);

    const NODE_BUDGET = 100, MAX_FAN = 50;
    const stride = Math.max(1, Math.ceil(N / MAX_FAN));
    const shown: number[] = [];
    for (let i = 0; i < N; i += stride) shown.push(i);
    if (shown[shown.length - 1] !== N - 1) shown.push(N - 1);
    const LEAVES_PER_GEN = Math.max(1, Math.floor(NODE_BUDGET / shown.length));

    const radialLink = (rIn: number, thIn: number, rOut: number, thOut: number) => {
      const Ax = rIn * Math.cos(thIn), Ay = rIn * Math.sin(thIn);
      const Bx = rIn * Math.cos(thOut), By = rIn * Math.sin(thOut);
      const Cx = rOut * Math.cos(thOut), Cy = rOut * Math.sin(thOut);
      let d = thOut - thIn; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI;
      const sweep = d >= 0 ? 1 : 0;
      return `M ${Ax.toFixed(2)} ${Ay.toFixed(2)} A ${rIn.toFixed(2)} ${rIn.toFixed(2)} 0 0 ${sweep} ${Bx.toFixed(2)} ${By.toFixed(2)} L ${Cx.toFixed(2)} ${Cy.toFixed(2)}`;
    };

    for (const i of shown) {
      const r = lin[i];
      const thMean = angleOf(meanProj[i][0], meanProj[i][1]);
      const rMean = radiusMean(r.gen), rLeaf = radiusLeaf(r.gen);
      const projs = leafProjByGen[i];
      const fits = fitsOf(r);
      const idx = projs.map((_, k) => k).sort((a, b) => fits[b] - fits[a]).slice(0, LEAVES_PER_GEN);
      const bestK = idx.length ? idx[0] : -1;
      for (const k of idx) {
        const thLeaf = angleOf(projs[k][0], projs[k][1]);
        const col = fitnessColorRgb((fits[k] - fMin) / fRange);
        svg.appendChild(el("path", { d: radialLink(rMean, thMean, rLeaf, thLeaf), fill: "none", stroke: col, "stroke-width": "0.45", "stroke-opacity": "0.5", "stroke-linecap": "round" }));
        const [bx, by] = polar(projs[k][0], projs[k][1], rLeaf);
        const bc = el("circle", { cx: bx.toFixed(2), cy: by.toFixed(2), r: k === bestK ? "2.0" : "1.1", fill: col, stroke: "#0f0f0f", "stroke-width": "0.35" });
        const title = document.createElementNS(NS, "title");
        title.textContent = `gen ${r.gen} · ${k === bestK ? "best" : "rank " + k} · fitness ${fits[k].toFixed(4)}`;
        bc.appendChild(title);
        svg.appendChild(bc);
      }
      const [mx, my] = meanPts[i];
      svg.appendChild(el("circle", { cx: mx.toFixed(2), cy: my.toFixed(2), r: "0.9", fill: "#4a9eff", stroke: "#0f0f0f", "stroke-width": "0.25" }));
    }
    svg.appendChild(el("circle", { cx: "0", cy: "0", r: "1.2", fill: "#c8c8c8" }));

    const last = lineage[lineage.length - 1];
    const leaves = last.top_k && last.top_k.length > 1 ? last.top_k.map((t) => t.genome) : null;
    let div = "—";
    if (leaves) {
      let sum = 0, pairs = 0;
      for (let a = 0; a < leaves.length; a++) for (let b = a + 1; b < leaves.length; b++) { sum += stdEuclid(basis, leaves[a], leaves[b]); pairs++; }
      div = (sum / pairs).toFixed(3);
    }
    setStats({
      gen: last.gen + 1, best: last.fitness_best_so_far, genBest: last.fitness_gen_best,
      genMean: last.fitness_gen_mean, sigma: last.sigma, div,
      drift: N >= 2 ? stdEuclid(basis, lineage[0].mean, last.mean).toFixed(3) : "0.000",
      pc1: (basis.explainedPc1 * 100).toFixed(1), pc2: (basis.explainedPc2 * 100).toFixed(1),
      dim: last.mean.length, k: leaves ? leaves.length : 1, frozen: lineage.length >= WARMUP,
    });
  }, [lineage]);

  return (
    <div className="phylo">
      <div className="viz-wrap">
        {lineage.length === 0 && <div className="viz-empty">waiting for lineage…</div>}
        <svg ref={svgRef} viewBox="-112 -112 224 224" className="phylo-svg" />
      </div>
      {stats && (
        <div className="phylo-stats mono">
          <span><i>gen</i> {stats.gen}</span><span><i>best</i> {stats.best.toFixed(4)}</span>
          <span><i>gen best</i> {stats.genBest.toFixed(4)}</span><span><i>gen mean</i> {stats.genMean.toFixed(4)}</span>
          <span><i>σ</i> {stats.sigma.toFixed(3)}</span><span><i>diversity</i> {stats.div}</span>
          <span><i>drift</i> {stats.drift}</span>
          <span><i>PC1/PC2</i> {stats.pc1}%/{stats.pc2}%</span>
          <span><i>dim</i> {stats.dim}</span><span><i>K</i> {stats.k}</span>
          <span><i>basis</i> <b style={{ color: stats.frozen ? "var(--good)" : "var(--warn)" }}>{stats.frozen ? "frozen" : "warming"}</b></span>
        </div>
      )}
    </div>
  );
}
